import { useEffect, useRef, useState } from "react";
import { dataApi } from "@/lib/api";
import { PageHeader } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Sparkles, Bot, User as UserIcon, Plus } from "lucide-react";

const SUGGESTIONS = [
  "Which employee has the lowest productivity?",
  "Show me all projects that are over budget",
  "Who is overloaded right now?",
  "Summarize today's engineering activity",
  "Which project has the best health score?",
  "Recommend workload redistribution across teams",
];

export default function AIManager() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Ask me anything about your workforce, projects, risks, or performance metrics." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);

  const loadSessions = () => dataApi.chatSessions().then((r) => setSessions(r.data));

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await dataApi.chat(q, sessionId);
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      loadSessions();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't answer that right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const openSession = async (sid) => {
    setSessionId(sid);
    const { data } = await dataApi.chatMessages(sid);
    setMessages(data.map((m) => ({ role: m.role, content: m.content })));
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([{ role: "assistant", content: "New conversation. What would you like to explore?" }]);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="ai-manager-header"
        title="AI Manager Agent"
        subtitle="Conversational access to your entire organisation"
      />

      <div className="grid lg:grid-cols-4 gap-4 h-[calc(100vh-260px)]">
        {/* Sessions sidebar */}
        <div className="hidden lg:flex flex-col data-card p-3">
          <Button data-testid="ai-new-session" onClick={newSession} className="w-full mb-3"><Plus className="w-4 h-4 mr-1" />New chat</Button>
          <div className="text-xs label-caps px-2 mb-2">Recent</div>
          <div className="flex-1 overflow-y-auto scroll-thin">
            <div className="space-y-1">
              {sessions.map((s) => (
                <button
                  key={s.id}
                  data-testid={`session-${s.id}`}
                  onClick={() => openSession(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm truncate hover:bg-accent ${sessionId === s.id ? "bg-accent" : ""}`}
                >
                  {s.title}
                </button>
              ))}
              {sessions.length === 0 && <div className="text-xs text-muted-foreground px-2">No conversations yet</div>}
            </div>
          </div>
        </div>

        {/* Main chat */}
        <div className="lg:col-span-3 glass-strong rounded-2xl flex flex-col overflow-hidden">
          <div ref={scrollRef} className="flex-1 px-6 py-6 overflow-y-auto scroll-thin">
            <div className="space-y-5 max-w-3xl mx-auto">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                  <div className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center ${m.role === "user" ? "bg-primary/15" : "bg-gradient-to-br from-primary via-[hsl(262_83%_62%)] to-[hsl(340_82%_60%)]"}`}>
                    {m.role === "user" ? <UserIcon className="w-4 h-4 text-primary" /> : <Bot className="w-4 h-4 text-white" />}
                  </div>
                  <div className={`rounded-2xl px-5 py-3 text-sm leading-relaxed max-w-[80%] whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-secondary/70 rounded-tl-sm"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-[hsl(262_83%_62%)] flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>
                  <div className="bg-secondary/70 rounded-2xl px-4 py-3 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" />
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {messages.length <= 1 && (
            <div className="px-6 pb-3 flex flex-wrap gap-2 max-w-3xl mx-auto w-full">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`ai-suggest-${i}`}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 rounded-full hairline hover:bg-accent transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-4 border-t border-border/60 max-w-3xl mx-auto w-full flex gap-2"
          >
            <Input
              data-testid="ai-manager-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about employees, projects, risks…"
              disabled={loading}
              className="h-11"
            />
            <Button data-testid="ai-manager-send" type="submit" size="icon" className="h-11 w-11" disabled={loading || !input.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
